import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { api } from '@/utils/api';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { 
  ArrowLeft, TrendingUp, TrendingDown, Minus,
  Users, Target, BarChart3, 
  DollarSign, Building2, RefreshCw, MapPin,
  Calendar, ArrowUpRight, ArrowDownRight
} from 'lucide-react';
import { toast } from 'sonner';
import {
  AreaChart, Area, BarChart, Bar, XAxis, YAxis, 
  CartesianGrid, Tooltip, ResponsiveContainer, Legend
} from 'recharts';

const AdminAnalytics = ({ user }) => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [period, setPeriod] = useState('mtd');
  const [selectedCity, setSelectedCity] = useState('');
  const [cities, setCities] = useState([]);
  const [analytics, setAnalytics] = useState(null);
  
  // Trend data state
  const [trendType, setTrendType] = useState('daily');
  const [trendDays, setTrendDays] = useState(30);
  const [trendData, setTrendData] = useState(null);
  const [trendLoading, setTrendLoading] = useState(true);

  useEffect(() => {
    fetchAnalytics();
  }, [period, selectedCity]);

  useEffect(() => {
    fetchTrends();
  }, [trendType, trendDays, selectedCity]);

  const fetchAnalytics = async () => {
    setLoading(true);
    try {
      const params = { period };
      if (selectedCity) params.city = selectedCity;
      
      const response = await api.getAdminAnalytics(params);
      setAnalytics(response.data);
      
      if (response.data.available_cities) {
        setCities(response.data.available_cities);
      }
    } catch (error) {
      console.error('Failed to load analytics:', error);
      toast.error('Failed to load analytics');
    } finally {
      setLoading(false);
    }
  };

  const fetchTrends = async () => {
    setTrendLoading(true);
    try {
      const params = { trend_type: trendType, days: trendDays };
      if (selectedCity) params.city = selectedCity;
      
      const response = await api.getAnalyticsTrends(params);
      setTrendData(response.data);
    } catch (error) {
      console.error('Failed to load trends:', error);
    } finally {
      setTrendLoading(false);
    }
  };

  const formatCurrency = (amount) => {
    if (!amount || amount === 0) return '₹0';
    if (amount >= 10000000) {
      const crValue = amount / 10000000;
      return `₹${crValue % 1 === 0 ? crValue.toFixed(0) : parseFloat(crValue.toFixed(2))}Cr`;
    }
    if (amount >= 100000) {
      const lValue = amount / 100000;
      return `₹${lValue % 1 === 0 ? lValue.toFixed(0) : parseFloat(lValue.toFixed(1))}L`;
    }
    return `₹${amount.toLocaleString('en-IN')}`;
  };

  const formatChartValue = (value) => {
    if (value >= 10000000) return `${(value / 10000000).toFixed(1)}Cr`;
    if (value >= 100000) return `${(value / 100000).toFixed(0)}L`;
    if (value >= 1000) return `${(value / 1000).toFixed(0)}K`;
    return value;
  };

  const periodLabels = {
    mtd: 'Month to Date',
    qtd: 'Quarter to Date',
    ytd: 'Year to Date'
  };

  const scopeLabel = selectedCity || 'Pan India';

  const CustomTooltip = ({ active, payload, label }) => {
    if (active && payload && payload.length) {
      return (
        <div className="bg-white p-3 rounded-lg shadow-lg border border-slate-100">
          <p className="text-sm font-medium text-slate-700 mb-1">{label}</p>
          {payload.map((entry, index) => (
            <p key={index} className="text-sm" style={{ color: entry.color }}>
              {entry.name}: {entry.name === 'Sales' ? formatCurrency(entry.value) : entry.value}
            </p>
          ))}
        </div>
      );
    }
    return null;
  };

  return (
    <div className="min-h-screen bg-[#F8FAFC]">
      {/* Header */}
      <div className="bg-white border-b border-slate-100 px-4 py-4 sticky top-0 z-10">
        <div className="flex items-center gap-3 max-w-6xl mx-auto">
          <button 
            onClick={() => navigate('/')} 
            className="p-2 hover:bg-slate-100 rounded-full"
          >
            <ArrowLeft size={20} className="text-slate-600" />
          </button>
          <div className="flex-1">
            <h1 className="text-lg font-semibold text-[#111827]">Performance Analytics</h1>
            <p className="text-sm text-slate-500">{scopeLabel} • {periodLabels[period]}</p>
          </div>
          <Button
            variant="ghost"
            size="sm"
            onClick={() => { fetchAnalytics(); fetchTrends(); }}
            className="text-slate-600"
          >
            <RefreshCw size={16} className={loading ? 'animate-spin' : ''} />
          </Button>
        </div>
      </div>

      <div className="p-4 md:p-6 max-w-6xl mx-auto space-y-6">
        {/* Filters Row */}
        <div className="flex flex-wrap gap-2">
          {['mtd', 'qtd', 'ytd'].map((p) => (
            <button
              key={p}
              onClick={() => setPeriod(p)}
              className={`px-4 py-2 rounded-full text-sm font-medium transition-all ${
                period === p
                  ? 'bg-slate-900 text-white'
                  : 'bg-white text-slate-600 border border-slate-200 hover:border-slate-300'
              }`}
            >
              {p.toUpperCase()}
            </button>
          ))}
          
          <select
            value={selectedCity}
            onChange={(e) => setSelectedCity(e.target.value)}
            className={`px-4 py-2 rounded-full text-sm font-medium appearance-none pr-8 cursor-pointer transition-all ${
              selectedCity
                ? 'bg-slate-900 text-white'
                : 'bg-white text-slate-600 border border-slate-200 hover:border-slate-300'
            }`}
          >
            <option value="">Pan India</option>
            {cities.map((city) => (
              <option key={city} value={city}>{city}</option>
            ))}
          </select>
        </div>

        {loading ? (
          <div className="space-y-4">
            {[1, 2, 3].map((i) => (
              <Card key={i} className="p-6 bg-white animate-pulse">
                <div className="h-4 w-32 bg-slate-200 rounded mb-4"></div>
                <div className="h-8 w-24 bg-slate-200 rounded"></div>
              </Card>
            ))}
          </div>
        ) : analytics ? (
          <>
            {/* Key Metrics Grid */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              <Card className="p-4 bg-white border-0 shadow-sm rounded-xl">
                <div className="flex items-center gap-2 mb-2">
                  <div className="w-8 h-8 rounded-lg bg-emerald-100 flex items-center justify-center">
                    <DollarSign size={16} className="text-emerald-600" />
                  </div>
                  <span className="text-xs text-slate-500 font-medium">Approved Sales</span>
                </div>
                <p className="text-2xl font-bold text-slate-900">
                  {formatCurrency(analytics.total_approved_sales)}
                </p>
              </Card>

              <Card className="p-4 bg-white border-0 shadow-sm rounded-xl">
                <div className="flex items-center gap-2 mb-2">
                  <div className="w-8 h-8 rounded-lg bg-blue-100 flex items-center justify-center">
                    <Target size={16} className="text-blue-600" />
                  </div>
                  <span className="text-xs text-slate-500 font-medium">Approved Bookings</span>
                </div>
                <p className="text-2xl font-bold text-slate-900">
                  {analytics.approved_bookings || 0}
                </p>
                <p className="text-xs text-slate-400 mt-1">
                  of {analytics.total_bookings || 0} total
                </p>
              </Card>

              <Card className="p-4 bg-white border-0 shadow-sm rounded-xl">
                <div className="flex items-center gap-2 mb-2">
                  <div className="w-8 h-8 rounded-lg bg-purple-100 flex items-center justify-center">
                    <Users size={16} className="text-purple-600" />
                  </div>
                  <span className="text-xs text-slate-500 font-medium">Reps Contributing</span>
                </div>
                <p className="text-2xl font-bold text-slate-900">
                  {analytics.reps_with_bookings || 0}
                </p>
              </Card>

              <Card className="p-4 bg-white border-0 shadow-sm rounded-xl">
                <div className="flex items-center gap-2 mb-2">
                  <div className="w-8 h-8 rounded-lg bg-amber-100 flex items-center justify-center">
                    <BarChart3 size={16} className="text-amber-600" />
                  </div>
                  <span className="text-xs text-slate-500 font-medium">Avg Deal Size</span>
                </div>
                <p className="text-2xl font-bold text-slate-900">
                  {formatCurrency(analytics.avg_deal_size)}
                </p>
              </Card>
            </div>

            {/* Trend Charts Section */}
            <Card className="p-4 md:p-6 bg-white border-0 shadow-sm rounded-xl">
              <div className="flex flex-wrap items-center justify-between gap-3 mb-6">
                <h3 className="font-semibold text-slate-900 flex items-center gap-2">
                  <TrendingUp size={18} className="text-slate-500" />
                  Sales Trends
                </h3>
                <div className="flex gap-2">
                  {[
                    { value: 'daily', label: 'Daily' },
                    { value: 'weekly', label: 'Weekly' },
                    { value: 'monthly', label: 'Monthly' }
                  ].map((t) => (
                    <button
                      key={t.value}
                      onClick={() => setTrendType(t.value)}
                      className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-all ${
                        trendType === t.value
                          ? 'bg-slate-900 text-white'
                          : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
                      }`}
                    >
                      {t.label}
                    </button>
                  ))}
                  <select
                    value={trendDays}
                    onChange={(e) => setTrendDays(Number(e.target.value))}
                    className="px-3 py-1.5 rounded-lg text-xs font-medium bg-slate-100 text-slate-600 appearance-none cursor-pointer"
                  >
                    <option value={7}>7 Days</option>
                    <option value={14}>14 Days</option>
                    <option value={30}>30 Days</option>
                    <option value={60}>60 Days</option>
                    <option value={90}>90 Days</option>
                  </select>
                </div>
              </div>

              {/* Trend Summary Cards */}
              {trendData?.summary && (
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6">
                  <div className="p-3 bg-emerald-50 rounded-lg">
                    <p className="text-xs text-emerald-600 font-medium">Period Sales</p>
                    <p className="text-lg font-bold text-emerald-700">{formatCurrency(trendData.summary.total_sales)}</p>
                    <div className={`flex items-center gap-1 text-xs mt-1 ${
                      trendData.summary.sales_change_pct >= 0 ? 'text-emerald-600' : 'text-[#5B3DF5]'
                    }`}>
                      {trendData.summary.sales_change_pct >= 0 ? (
                        <ArrowUpRight size={12} />
                      ) : (
                        <ArrowDownRight size={12} />
                      )}
                      {Math.abs(trendData.summary.sales_change_pct)}% vs prev
                    </div>
                  </div>
                  <div className="p-3 bg-blue-50 rounded-lg">
                    <p className="text-xs text-blue-600 font-medium">Period Bookings</p>
                    <p className="text-lg font-bold text-blue-700">{trendData.summary.total_bookings}</p>
                    <div className={`flex items-center gap-1 text-xs mt-1 ${
                      trendData.summary.bookings_change_pct >= 0 ? 'text-emerald-600' : 'text-[#5B3DF5]'
                    }`}>
                      {trendData.summary.bookings_change_pct >= 0 ? (
                        <ArrowUpRight size={12} />
                      ) : (
                        <ArrowDownRight size={12} />
                      )}
                      {Math.abs(trendData.summary.bookings_change_pct)}% vs prev
                    </div>
                  </div>
                  <div className="p-3 bg-purple-50 rounded-lg">
                    <p className="text-xs text-purple-600 font-medium">Avg Daily Sales</p>
                    <p className="text-lg font-bold text-purple-700">{formatCurrency(trendData.summary.avg_daily_sales)}</p>
                  </div>
                  <div className="p-3 bg-amber-50 rounded-lg">
                    <p className="text-xs text-amber-600 font-medium">Avg Daily Bookings</p>
                    <p className="text-lg font-bold text-amber-700">{trendData.summary.avg_daily_bookings.toFixed(1)}</p>
                  </div>
                </div>
              )}

              {/* Sales Area Chart */}
              {trendLoading ? (
                <div className="h-64 flex items-center justify-center">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-slate-400"></div>
                </div>
              ) : trendData?.data && trendData.data.length > 0 ? (
                <div className="space-y-6">
                  {/* Sales Chart */}
                  <div>
                    <p className="text-xs text-slate-500 font-medium mb-3">Sales (₹)</p>
                    <ResponsiveContainer width="100%" height={220}>
                      <AreaChart data={trendData.data} margin={{ top: 5, right: 10, left: 0, bottom: 5 }}>
                        <defs>
                          <linearGradient id="salesGradient" x1="0" y1="0" x2="0" y2="1">
                            <stop offset="5%" stopColor="#10B981" stopOpacity={0.3}/>
                            <stop offset="95%" stopColor="#10B981" stopOpacity={0}/>
                          </linearGradient>
                        </defs>
                        <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" vertical={false} />
                        <XAxis 
                          dataKey="label" 
                          tick={{ fontSize: 10, fill: '#94A3B8' }} 
                          axisLine={{ stroke: '#E2E8F0' }}
                          tickLine={false}
                          interval="preserveStartEnd"
                        />
                        <YAxis 
                          tick={{ fontSize: 10, fill: '#94A3B8' }} 
                          axisLine={false}
                          tickLine={false}
                          tickFormatter={formatChartValue}
                          width={50}
                        />
                        <Tooltip content={<CustomTooltip />} />
                        <Area 
                          type="monotone" 
                          dataKey="sales" 
                          name="Sales"
                          stroke="#10B981" 
                          strokeWidth={2}
                          fill="url(#salesGradient)" 
                        />
                      </AreaChart>
                    </ResponsiveContainer>
                  </div>

                  {/* Bookings Bar Chart */}
                  <div>
                    <p className="text-xs text-slate-500 font-medium mb-3">Bookings Count</p>
                    <ResponsiveContainer width="100%" height={180}>
                      <BarChart data={trendData.data} margin={{ top: 5, right: 10, left: 0, bottom: 5 }}>
                        <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" vertical={false} />
                        <XAxis 
                          dataKey="label" 
                          tick={{ fontSize: 10, fill: '#94A3B8' }} 
                          axisLine={{ stroke: '#E2E8F0' }}
                          tickLine={false}
                          interval="preserveStartEnd"
                        />
                        <YAxis 
                          tick={{ fontSize: 10, fill: '#94A3B8' }} 
                          axisLine={false}
                          tickLine={false}
                          width={30}
                          allowDecimals={false}
                        />
                        <Tooltip content={<CustomTooltip />} />
                        <Bar 
                          dataKey="bookings" 
                          name="Bookings"
                          fill="#3B82F6" 
                          radius={[4, 4, 0, 0]}
                          maxBarSize={40}
                        />
                      </BarChart>
                    </ResponsiveContainer>
                  </div>
                </div>
              ) : (
                <div className="h-64 flex items-center justify-center text-slate-400">
                  <p>No trend data available for this period</p>
                </div>
              )}
            </Card>

            {/* City Breakdown - Only show for Pan India */}
            {!selectedCity && analytics.city_breakdown && analytics.city_breakdown.length > 0 && (
              <Card className="p-4 bg-white border-0 shadow-sm rounded-xl">
                <h3 className="font-semibold text-slate-900 mb-4 flex items-center gap-2">
                  <MapPin size={16} className="text-slate-500" />
                  City Breakdown
                </h3>
                <div className="space-y-3">
                  {analytics.city_breakdown.map((city, idx) => {
                    const maxSales = analytics.city_breakdown[0]?.total_sales || 1;
                    const percentage = (city.total_sales / maxSales) * 100;
                    
                    return (
                      <div 
                        key={city.city || idx} 
                        className="cursor-pointer hover:bg-slate-50 p-2 -mx-2 rounded-lg transition-colors"
                        onClick={() => setSelectedCity(city.city)}
                      >
                        <div className="flex items-center justify-between mb-1">
                          <div className="flex items-center gap-3">
                            <span className="text-sm font-medium text-slate-500 w-6">{idx + 1}.</span>
                            <span className="text-sm text-slate-900 font-medium">{city.city || 'Unknown'}</span>
                          </div>
                          <div className="text-right">
                            <p className="text-sm font-semibold text-slate-900">
                              {formatCurrency(city.total_sales)}
                            </p>
                            <p className="text-xs text-slate-400">{city.booking_count} bookings</p>
                          </div>
                        </div>
                        <div className="ml-9 h-1.5 bg-slate-100 rounded-full overflow-hidden">
                          <div 
                            className="h-full bg-gradient-to-r from-emerald-400 to-emerald-500 rounded-full transition-all duration-500"
                            style={{ width: `${percentage}%` }}
                          />
                        </div>
                      </div>
                    );
                  })}
                </div>
              </Card>
            )}

            {/* Top Performers */}
            {analytics.top_performers && analytics.top_performers.length > 0 && (
              <Card className="p-4 bg-white border-0 shadow-sm rounded-xl">
                <h3 className="font-semibold text-slate-900 mb-4 flex items-center gap-2">
                  <TrendingUp size={16} className="text-slate-500" />
                  Top Performers
                  <span className="text-xs text-slate-400 font-normal ml-auto">{scopeLabel}</span>
                </h3>
                <div className="space-y-3">
                  {analytics.top_performers.slice(0, 5).map((rep, idx) => (
                    <div key={rep.rep_id || idx} className="flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <span className={`text-sm font-bold w-6 ${
                          idx === 0 ? 'text-amber-500' : 
                          idx === 1 ? 'text-slate-400' : 
                          idx === 2 ? 'text-amber-600' : 'text-slate-400'
                        }`}>
                          #{idx + 1}
                        </span>
                        <div>
                          <span className="text-sm text-slate-900">{rep.rep_name}</span>
                          {rep.city && <span className="text-xs text-slate-400 ml-2">{rep.city}</span>}
                        </div>
                      </div>
                      <div className="text-right">
                        <p className="text-sm font-semibold text-slate-900">
                          {formatCurrency(rep.total_sales)}
                        </p>
                        <p className="text-xs text-slate-400">{rep.booking_count} bookings</p>
                      </div>
                    </div>
                  ))}
                </div>
              </Card>
            )}

            {/* Period Info */}
            <div className="text-center py-2">
              <p className="text-xs text-slate-400">
                {periodLabels[period]} • {scopeLabel} • {new Date().toLocaleDateString('en-IN', { 
                  day: 'numeric', 
                  month: 'short', 
                  year: 'numeric' 
                })}
              </p>
            </div>
          </>
        ) : (
          <Card className="p-12 text-center bg-white border-0 shadow-sm rounded-xl">
            <BarChart3 size={48} className="mx-auto mb-4 text-slate-300" />
            <p className="text-slate-500 font-medium">No analytics data available</p>
            <p className="text-slate-400 text-sm mt-1">Data will appear once there are approved bookings</p>
          </Card>
        )}
      </div>
    </div>
  );
};

export default AdminAnalytics;
